import {
  Play,
  Square,
  AlertTriangle,
  WifiOff,
  Pause,
  Building,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext.jsx";
import { cn } from "@/lib/utils.js";
import { ALL_STATUSES } from "@/hooks/useFilters.js";
import { getStatusStyle } from "@/lib/status-styles.js";
import { calculateFleetStats } from "@/lib/asset-data-utils.js";

/**
 * @typedef {Object} FleetStats
 * @property {number} normal - Number of normal compressors
 * @property {number} offline - Number of offline compressors
 * @property {number} danger - Number of danger compressors
 * @property {number} alerted - Number of alerted compressors
 * @property {number} nodata - Number of no data compressors
 * @property {number} standby - Number of standby compressors
 * @property {number} total - Total number of compressors
 */

/**
 * Get status border class based on status
 * @param {string} status - The status type
 * @param {boolean} isActive - Whether the status is currently active/selected
 * @returns {string} Border class string
 */
const getStatusBorderClass = (status, isActive) => {
  const statusStyle = getStatusStyle(status || "all", isActive);
  return statusStyle.cardClass;
};

/** Props expected to be passed in for FleetStatusCards component.
 * @typedef {Object} FleetStatusCardsProps
 * @property {Function} onStatusClick - Callback when a status card is clicked
 * @property {string[]} activeFilters - Currently active status filters
 * @property {Object} [filters] - Current filter state for vendor, location, telemetry
 * @property {string} [searchTerm] - Search term to filter assets by
 */

/**
 * Fleet Status Cards component that displays fleet statistics as interactive cards
 * @param {FleetStatusCardsProps} props - Component props
 * @returns {JSX.Element} Grid of status cards
 */
export default function FleetStatusCards({
  onStatusClick,
  activeFilters = [],
  filters = {},
  searchTerm = "",
}) {
  // Use global asset context instead of separate API call
  const { isLoading } = useAssets();

  // Get filtered assets excluding status filter, then calculate stats from all statuses
  const filtersWithoutStatus = { ...filters, status: ALL_STATUSES };
  const { filteredAssets } = useFilteredAssets(
    filtersWithoutStatus,
    searchTerm
  );

  // Calculate stats from all filtered assets (all statuses)
  const stats = calculateFleetStats(filteredAssets);

  if (isLoading) {
    return (
      <div className='grid grid-cols-1 lg:grid-cols-7 gap-4 mb-6'>
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className='bg-muted animate-pulse h-24 rounded-lg border border-border'
          />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: "Normal",
      value: stats?.normal || 0,
      icon: Play,
      status: "normal",
      clickable: true,
    },
    {
      label: "Offline",
      value: stats?.offline || 0,
      icon: Square,
      status: "offline",
      clickable: true,
    },
    {
      label: "Danger",
      value: stats?.danger || 0,
      icon: AlertTriangle,
      status: "danger",
      clickable: true,
    },
    {
      label: "Alerted",
      value: stats?.alerted || 0,
      icon: AlertTriangle,
      status: "alerted",
      clickable: true,
    },
    {
      label: "No Data",
      value: stats?.nodata || 0,
      icon: WifiOff,
      status: "nodata",
      clickable: true,
    },
    {
      label: "Standby",
      value: stats?.standby || 0,
      icon: Pause,
      status: "standby",
      clickable: true,
    },
    {
      label: "Total Fleet",
      value: stats?.total || 0,
      icon: Building,
      status: "all",
      clickable: true,
    },
  ];

  /**
   * Handles card click events
   * @param {{ status: string, clickable: boolean }} card - The card that was clicked
   */
  const handleCardClick = (card) => {
    if (!onStatusClick || !card.clickable) return;
    onStatusClick(card.status === "all" ? "all" : card.status);
  };

  return (
    <div className='grid grid-cols-3 lg:grid-cols-7 gap-4 mb-6'>
      {cards.map((card) => {
        const Icon = card.icon;
        const isActive =
          card.status === "all"
            ? activeFilters.length === ALL_STATUSES.length
              && ALL_STATUSES.every((status) => activeFilters.includes(status))
            : activeFilters.includes(card.status)
              && activeFilters.length < ALL_STATUSES.length;
        const style = getStatusStyle(card.status, isActive);
        const isTotalFleet = card.status === "all";
        const cardClass = cn(
          "shadow-sm transition-all duration-200",
          getStatusBorderClass(card.status, isActive),
          card.clickable && "cursor-pointer hover-elevate"
        );

        const labelClass = isActive
          ? isTotalFleet
            ? "text-[#0E1116] dark:text-white"
            : "text-white"
          : style.labelColor;
        const valueClass = isActive
          ? isTotalFleet
            ? "text-[#0E1116] dark:text-white"
            : "text-white"
          : "text-foreground";
        const iconClass = isActive
          ? isTotalFleet
            ? "text-[#0E1116] dark:text-white"
            : "text-white"
          : style.iconColor;

        return (
          <Card
            key={card.label}
            className={cardClass}
            onClick={() => handleCardClick(card)}
            data-testid={`status-card-${card.status}`}>
            <CardContent className='p-3'>
              <div className='flex items-center justify-between'>
                <div>
                  <p
                    className={`text-xs font-medium uppercase tracking-wide ${labelClass}`}>
                    {card.label}
                  </p>
                  <p className={`text-xl font-semibold ${valueClass}`}>
                    {card.value}
                  </p>
                </div>
                <Icon className={`${iconClass} opacity-80`} size={20} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
